'use client';

import {
  ManifestBirthdateField,
  ManifestCheckboxField,
  ManifestInputField,
  ManifestSelectField,
} from '../fields';
import { ManifestSection } from '../ManifestSection';
import { mapManifestOptionsToHtmlOptions } from '../utils';

import type { Manifest, ManifestFormValues } from '../types';
import type { SimpleFormApi } from '@lib/form/helper';
import type { AnyFieldApi } from '@tanstack/react-form';
import type { I18NInterface } from '@tuic/web-compositions/utils/i18n';
import type { FC } from 'react';

export type IdCardSectionProps = {
  form: SimpleFormApi<ManifestFormValues>;
  manifest?: Manifest;
  isDisabled?: boolean;
  entryRequirementsHref?: string;
  ukCruise?: boolean;

  i18n?: I18NInterface<
    | 'idCardTitle'
    | 'idCardDescription'
    | 'idCardType'
    | 'idCardNationality'
    | 'idCardNumber'
    | 'idCardBirthPlace'
    | 'idCardIssuingPlace'
    | 'idCardBirthDate'
    | 'idCardIssuingDate'
    | 'idCardBirthName'
    | 'idCardValidUntil'
    | 'idCardProfession'
    | 'idCardProfessionNote'
    | 'idCardEntryRequirementsTitle'
    | 'idCardEntryRequirementsConfirmBefore'
    | 'idCardEntryRequirementsConfirmLink'
    | 'idCardEntryRequirementsConfirmAfter'
    | 'idCardVisaConfirm'
    | 'idCardHasETALabel'
    | 'idCardSelectPlaceholder'
  >;
};

export const IdCardSection: FC<IdCardSectionProps> = ({
  form,
  i18n,
  manifest,
  isDisabled,
  entryRequirementsHref,
  ukCruise,
}) => {
  const credentialOptions = manifest?.manifestOptions?.credentials;
  const now = new Date().getFullYear();

  return (
    <ManifestSection pictogram="blog" headline={i18n?.idCardTitle}>
      <p>{i18n?.idCardDescription}</p>

      <div className="flex flex-col gap-4 md:flex-row md:gap-8">
        <form.Field name="credentials.passportType">
          {(field: AnyFieldApi) => (
            <ManifestSelectField
              field={field}
              required
              label={i18n?.idCardType}
              placeholder={i18n?.idCardSelectPlaceholder}
              options={mapManifestOptionsToHtmlOptions(
                credentialOptions?.passportTypeOptions,
              )}
              className="w-full"
              disabled={isDisabled}
            />
          )}
        </form.Field>
        <form.Field name="credentials.nationality">
          {(field: AnyFieldApi) => (
            <ManifestSelectField
              field={field}
              required
              label={i18n?.idCardNationality}
              placeholder={i18n?.idCardSelectPlaceholder}
              options={mapManifestOptionsToHtmlOptions(
                credentialOptions?.nationalityOptions,
              )}
              className="w-full"
              disabled={isDisabled}
            />
          )}
        </form.Field>
      </div>
      <div className="flex flex-col gap-4 md:flex-row md:gap-8">
        <form.Field name="credentials.passportNumber">
          {(field: AnyFieldApi) => (
            <ManifestInputField
              field={field}
              required
              label={i18n?.idCardNumber}
              className="w-full"
              disabled={isDisabled}
            />
          )}
        </form.Field>
        <form.Field name="credentials.placeOfBirth">
          {(field: AnyFieldApi) => (
            <ManifestInputField
              field={field}
              required
              label={i18n?.idCardBirthPlace}
              className="w-full"
              disabled={isDisabled}
            />
          )}
        </form.Field>
      </div>
      <div className="flex flex-col gap-4 md:flex-row md:gap-8">
        <form.Field name="credentials.placeOfIssue">
          {(field: AnyFieldApi) => (
            <ManifestInputField
              field={field}
              required
              label={i18n?.idCardIssuingPlace}
              className="w-full"
              disabled={isDisabled}
            />
          )}
        </form.Field>
        <form.Field name="credentials.dateOfBirth">
          {(field: AnyFieldApi) => (
            <ManifestBirthdateField
              field={field}
              required
              label={i18n?.idCardBirthDate}
              minYear={now - 100}
              maxYear={now}
              className="w-full"
              disabled={isDisabled}
            />
          )}
        </form.Field>
      </div>
      <div className="flex flex-col gap-4 md:flex-row md:gap-8">
        <form.Field name="credentials.dateOfIssue">
          {(field: AnyFieldApi) => (
            <ManifestBirthdateField
              field={field}
              required
              label={i18n?.idCardIssuingDate}
              maxYear={now}
              className="w-full"
              disabled={isDisabled}
            />
          )}
        </form.Field>
        <form.Field name="credentials.birthName">
          {(field: AnyFieldApi) => (
            <ManifestInputField
              field={field}
              label={i18n?.idCardBirthName}
              className="w-full"
              disabled={isDisabled}
            />
          )}
        </form.Field>
      </div>
      <div className="flex flex-col gap-4 md:flex-row md:gap-8">
        <form.Field name="credentials.validUntil">
          {(field: AnyFieldApi) => (
            <ManifestBirthdateField
              field={field}
              required
              label={i18n?.idCardValidUntil}
              minYear={now}
              maxYear={now + 1}
              className="w-full"
              disabled={isDisabled}
            />
          )}
        </form.Field>
        <form.Field name="credentials.profession">
          {(field: AnyFieldApi) => (
            <ManifestSelectField
              field={field}
              required
              label={i18n?.idCardProfession}
              note={i18n?.idCardProfessionNote}
              placeholder={i18n?.idCardSelectPlaceholder}
              options={mapManifestOptionsToHtmlOptions(
                credentialOptions?.professionOptions,
              )}
              className="w-full"
              disabled={isDisabled}
            />
          )}
        </form.Field>
      </div>

      <div className="mt-4 space-y-4">
        <h3 className="headline-md font-normal">
          {i18n?.idCardEntryRequirementsTitle}*
        </h3>
        <form.Field name="credentials.entryRequirementsAccepted">
          {(field: AnyFieldApi) => (
            <ManifestCheckboxField
              field={field}
              label={
                <>
                  {i18n?.idCardEntryRequirementsConfirmBefore}
                  {entryRequirementsHref ? (
                    <a
                      href={entryRequirementsHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                    >
                      {i18n?.idCardEntryRequirementsConfirmLink}
                    </a>
                  ) : (
                    i18n?.idCardEntryRequirementsConfirmLink
                  )}
                  {i18n?.idCardEntryRequirementsConfirmAfter}
                </>
              }
              disabled={isDisabled}
            />
          )}
        </form.Field>
        <form.Field name="credentials.hasVisa">
          {(field: AnyFieldApi) => (
            <ManifestCheckboxField
              field={field}
              label={i18n?.idCardVisaConfirm}
              disabled={isDisabled}
            />
          )}
        </form.Field>
        {ukCruise && (
          <form.Field name="credentials.hasETA">
            {(field: AnyFieldApi) => (
              <ManifestCheckboxField
                field={field}
                label={i18n?.idCardHasETALabel}
                disabled={isDisabled}
              />
            )}
          </form.Field>
        )}
      </div>
    </ManifestSection>
  );
};
